import { useListActionsPageQueryControllerListPage } from "@/generated/follow-up-actions/follow-up-actions";
import type { FollowUpActionResponseDto } from "@/generated/model/followUpActionResponseDto";
import type { FollowUpActionResponseDtoDueStatus } from "@/generated/model/followUpActionResponseDtoDueStatus";
import type { ListActionsPageQueryControllerListPageAssigneeRole } from "@/generated/model/listActionsPageQueryControllerListPageAssigneeRole";
import type { ListActionsPageQueryControllerListPageDueStatus } from "@/generated/model/listActionsPageQueryControllerListPageDueStatus";
import type { ListActionsPageQueryControllerListPageSortBy } from "@/generated/model/listActionsPageQueryControllerListPageSortBy";
import type { ListActionsPageQueryControllerListPageSortDir } from "@/generated/model/listActionsPageQueryControllerListPageSortDir";
import type { ListActionsPageQueryControllerListPageTab } from "@/generated/model/listActionsPageQueryControllerListPageTab";
import type { SortDir } from "@/lib/category-display";
import {
  apiTabFor,
  type AssigneeFilter,
  type TaskManagerSortColumn,
  type TaskManagerTab,
} from "@/lib/task-manager";

interface UseTaskManagerDataArgs {
  nurseryId?: string;
  tab: TaskManagerTab;
  categoryId?: string;
  assignee?: AssigneeFilter | null;
  dueStatus?: FollowUpActionResponseDtoDueStatus;
  search?: string;
  sortBy: TaskManagerSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

export interface TaskManagerCounts {
  followUpActions: number;
  tasks: number;
  awaitingAcknowledgement: number;
  history: number;
}

/**
 * Drives the Task Manager list server-side: forwards tab, category, assignee,
 * due status, search, sort and pagination to the paginated endpoint and reads
 * back the page plus all four tab count badges (the server computes the counts
 * under the same filters, so a badge always matches what switching to that tab
 * would show).
 *
 * The general-tasks tab has no server-side source yet, so it resolves to no API
 * tab and the query is disabled — the tab renders empty without a request.
 */
export function useTaskManagerData({
  nurseryId,
  tab,
  categoryId,
  assignee,
  dueStatus,
  search,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseTaskManagerDataArgs) {
  const apiTab = apiTabFor(tab);
  const enabled = !!nurseryId && apiTab !== null;

  const listQuery = useListActionsPageQueryControllerListPage(
    nurseryId ?? "",
    {
      tab: (apiTab ?? "open") as ListActionsPageQueryControllerListPageTab,
      categoryId: categoryId || undefined,
      assigneeUserId: assignee?.kind === "user" ? assignee.userId : undefined,
      assigneeRole:
        assignee?.kind === "role"
          ? (assignee.role as ListActionsPageQueryControllerListPageAssigneeRole)
          : undefined,
      dueStatus: dueStatus as
        | ListActionsPageQueryControllerListPageDueStatus
        | undefined,
      search: search || undefined,
      sortBy: sortBy as ListActionsPageQueryControllerListPageSortBy,
      sortDir: sortDir as ListActionsPageQueryControllerListPageSortDir,
      limit: pageSize,
      offset: page * pageSize,
    },
    // refetchOnMount "always": the global QueryClient defaults (staleTime 5min,
    // refetchOnMount false) would otherwise replay a cached page after an action
    // is signed off elsewhere — leaving a stale row in the wrong tab.
    { query: { enabled, refetchOnMount: "always" } },
  );

  const items: FollowUpActionResponseDto[] = enabled
    ? (listQuery.data?.data.data ?? [])
    : [];
  const total: number = enabled ? (listQuery.data?.data.meta.total ?? 0) : 0;
  const serverCounts = listQuery.data?.data.meta.counts;

  const counts: TaskManagerCounts = {
    followUpActions: serverCounts?.open ?? 0,
    // Always zero until general tasks exist; there is nothing to count yet.
    tasks: 0,
    awaitingAcknowledgement: serverCounts?.requiresSignOff ?? 0,
    history: serverCounts?.history ?? 0,
  };

  return {
    items,
    total,
    isLoading: enabled && listQuery.isLoading,
    isError: listQuery.isError,
    counts,
  };
}
